import React, { useState } from "react";
import {
    BookOpen, LayoutDashboard, Users, ShieldCheck, KanbanSquare, ListChecks,
    Repeat, Copy, CheckSquare, MessageCircle, Paperclip, CalendarDays,
    MessageSquare, HandHelping, Bell, BarChart3, Settings, Database, Lightbulb,
    Crown, Clock, Pin, Filter, GripVertical
} from "lucide-react";

/* Etichetă de rol colorată */
function Role({ children, color = "#002FA7" }) {
    return (
        <span className="inline-block text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded mr-1"
            style={{ backgroundColor: color + "1A", color }}>
            {children}
        </span>
    );
}

/* Listă cu bullet-uri */
function UL({ children }) {
    return <ul className="list-disc pl-5 space-y-1.5 text-sm text-[#374151] dark:text-[#C7CDD6] leading-relaxed">{children}</ul>;
}

function P({ children }) {
    return <p className="text-sm text-[#374151] dark:text-[#C7CDD6] leading-relaxed mb-3">{children}</p>;
}

function Tip({ children }) {
    return (
        <div className="flex items-start gap-2 bg-[#FFFBEB] dark:bg-[#2a2410] border border-[#FDE68A] dark:border-[#5c4d12] rounded-md px-3 py-2 mt-3">
            <Lightbulb size={15} className="text-[#D97706] flex-shrink-0 mt-0.5" />
            <p className="text-xs text-[#92400E] dark:text-[#FCD34D] leading-relaxed">{children}</p>
        </div>
    );
}

const SECTIONS = [
    {
        id: "intro", icon: <BookOpen size={18} />, title: "Ce este TOM",
        body: (
            <>
                <P><strong>TOM (Team Operations Manager)</strong> este platforma internă de management operațional — un sistem Kanban prin care echipele își organizează munca pe taskuri, cu responsabilitate clară și monitorizare managerială. Totul este în limba română.</P>
                <P>Structura platformei, de la mare la mic:</P>
                <UL>
                    <li><strong>Workspace</strong> (spațiu de lucru) — cel mai larg container, ex. un departament sau un proiect mare.</li>
                    <li><strong>Echipă</strong> — grup de oameni dintr-un workspace.</li>
                    <li><strong>Board</strong> (panou Kanban) — tablă cu coloane, alocată uneia sau mai multor echipe.</li>
                    <li><strong>Task</strong> — o sarcină concretă pe board, care se mută între coloane pe măsură ce avansează.</li>
                    <li><strong>Checklist</strong> — punctele (sub-pașii) din interiorul unui task.</li>
                </UL>
            </>
        ),
    },
    {
        id: "roluri", icon: <ShieldCheck size={18} />, title: "Roluri și permisiuni",
        body: (
            <>
                <P>Există trei roluri globale plus o atribuire specială per echipă:</P>
                <UL>
                    <li><Role color="#7C3AED">Super Admin</Role> acces complet la tot, inclusiv setări de sistem și backup.</li>
                    <li><Role color="#002FA7">Manager Departament</Role> (rol „admin”) gestionează workspace-uri, echipe, board-uri, utilizatori și roluri.</li>
                    <li><Role color="#0D9488">Membru</Role> vede echipele și board-urile din care face parte; își lucrează taskurile (creează/mută/actualizează în limitele permise), dar nu administrează.</li>
                    <li><Role color="#D97706">Team Lead</Role> NU este un rol global, ci o <strong>atribuire pe o anumită echipă</strong>. Lead-ul își poate gestiona echipa condusă (membri, board-uri, taskuri, checklist-uri, recurență), dar doar pentru echipa lui.</li>
                </UL>
                <Tip>Membrii au câmpuri restricționate pe task (responsabil, prioritate, termen, descriere) — le văd, dar doar adminii/lead-ul le pot modifica. Membrii pot oricând să bifeze checklist-ul, să comenteze și să atașeze fișiere.</Tip>
            </>
        ),
    },
    {
        id: "navigare", icon: <LayoutDashboard size={18} />, title: "Navigare & Dashboard",
        body: (
            <>
                <UL>
                    <li><strong>Meniul din stânga</strong> — acces la Dashboard, Calendar, Chat, Asistență, Rapoarte, Notificări, Setări și acest Ghid. Adminii au în plus „Toate taskurile” și „Manager Departament”. Poți restrânge meniul cu săgeata din jos.</li>
                    <li><strong>Dashboard</strong> — prima pagină: 5 carduri cu statistici (Active, Întârziate, Finalizate săptămâna, Blocate, Scadente azi). Click pe un card filtrează lista „Taskurile mele”.</li>
                    <li><strong>Temă întunecată / luminoasă</strong> — butonul soare/lună din bara de sus. Implicit e întunecat; alegerea se ține minte.</li>
                    <li><strong>Workspace-urile tale</strong> apar în meniu; din ele intri în echipe și board-uri.</li>
                </UL>
            </>
        ),
    },
    {
        id: "workspaces", icon: <Users size={18} />, title: "Workspace-uri & Echipe",
        body: (
            <>
                <P><Role color="#002FA7">Manager Departament</Role> Crearea și administrarea structurii:</P>
                <UL>
                    <li><strong>Workspace nou</strong> — butonul „+” din meniu. Îl poți edita (nume, descriere) sau șterge.</li>
                    <li><strong>Echipă nouă</strong> — din pagina workspace-ului. Are nume, descriere și un icon la alegere (din 25).</li>
                    <li><strong>Adăugare membri</strong> — în pagina echipei, modalul are căutare după nume/email, bife, „selectează toți” și adăugare în bloc.</li>
                    <li><strong>Team Lead</strong> <Crown size={12} className="inline text-[#D97706]" /> — pe pagina echipei, butonul ⭐ promovează un membru ca lead; lead-ul curent are badge auriu „TEAM LEAD”. Tot adminul îl poate retrograda.</li>
                    <li><strong>Membri detaliați</strong> — secțiune (admin/lead) cu statistici per persoană: taskuri active / întârziate / finalizate + lista lor.</li>
                </UL>
            </>
        ),
    },
    {
        id: "dashboard-boards", icon: <LayoutDashboard size={18} />, title: "Dashboard workspace — organizarea board-urilor",
        body: (
            <>
                <P>Când intri pe un workspace vezi echipele sus, iar dedesubt board-urile, fiecare ca o coloană cu primele taskuri din ele. Ai mai multe unelte ca să-ți aranjezi vederea exact cum vrei:</P>
                <UL>
                    <li><Filter size={13} className="inline -mt-0.5 text-[#002FA7]" /> <strong>Filtrare pe echipă</strong> — sub echipe, secțiunea „Alege echipa a căror board-uri vrei să le vezi” are un buton pentru fiecare echipă (+ „Toate echipele”). Implicit se afișează board-urile unei singure echipe, ca să nu fie aglomerat. Alegerea ta e <strong>reținută</strong> și revine automat data viitoare.</li>
                    <li><GripVertical size={13} className="inline -mt-0.5 text-[#9CA3AF]" /> <strong>Reordonare (drag-and-drop)</strong> — prinde board-ul de mânerul cu puncte din stânga titlului și trage-l ca să schimbi ordinea. (Restul titlului rămâne pentru deschiderea board-ului.)</li>
                    <li><Pin size={13} className="inline -mt-0.5 text-[#002FA7]" /> <strong>Fixare („pin”)</strong> — apasă pe pictograma 📌 de pe un board ca să-l fixezi. Board-urile fixate apar într-o secțiune <strong>„Fixate”</strong> sus și sunt <strong>vizibile mereu, indiferent de echipa selectată</strong> — ideale pentru board-urile pe care le folosești cel mai des, chiar dacă sunt din echipe diferite. Le poți reordona și pe ele între ele; reapasă 📌 ca să le defixezi.</li>
                    <li><strong>Taskuri pe board</strong> — fiecare coloană arată ~5 taskuri; dacă sunt mai multe, faci scroll în jos în interiorul board-ului. Click pe un task îl deschide direct.</li>
                </UL>
                <Tip>Ordinea și board-urile fixate sunt <strong>personale</strong> (doar tu le vezi) și se <strong>sincronizează automat</strong> — apar la fel pe orice calculator te-ai loga.</Tip>
            </>
        ),
    },
    {
        id: "boards", icon: <KanbanSquare size={18} />, title: "Board-uri (panouri Kanban)",
        body: (
            <>
                <UL>
                    <li><strong>Creare</strong> <Role color="#002FA7">Admin</Role><Role color="#D97706">Lead</Role> — un board poate fi alocat mai multor echipe simultan.</li>
                    <li><strong>Editare / Arhivare</strong> — iconițele creion și arhivă din antetul board-ului. Arhivarea ascunde board-ul fără a-l șterge.</li>
                    <li><strong>Duplicare board</strong> — butonul „Copy” copiază coloanele și etichetele (opțional și taskurile).</li>
                    <li><strong>Șabloane de board</strong> — salvează structura unui board ca șablon și creează rapid board-uri noi din el.</li>
                    <li><strong>Permisiuni membri</strong> — în setările board-ului poți activa „Permite membrilor să creeze taskuri”. Implicit doar adminii/lead-ul creează.</li>
                    <li><strong>Coloane speciale</strong> — „Trimis la CTC” se activează per board din setări (pentru control tehnic de calitate).</li>
                    <li><strong>Filtre</strong> — pe board poți filtra după prioritate, responsabil și căuta după titlu.</li>
                </UL>
            </>
        ),
    },
    {
        id: "coloane", icon: <ListChecks size={18} />, title: "Coloanele și regulile de mutare",
        body: (
            <>
                <P>Fiecare board are coloanele standard plus câteva speciale. Taskurile se mută prin tragere (drag &amp; drop):</P>
                <UL>
                    <li><strong>De făcut → În lucru → Review → Finalizat</strong> — fluxul normal de lucru.</li>
                    <li><strong>Stand By</strong> (roșu) — taskuri blocate / în așteptare de decizie. Doar admin sau Team Lead pot muta aici; responsabilii primesc notificare.</li>
                    <li><strong>Trimis la CTC</strong> (mov) — opțională, pentru control tehnic de calitate. Tot doar admin/lead.</li>
                    <li><strong>Recurent</strong> (albastru) — „sursa” taskurilor care se repetă (vezi secțiunea Taskuri recurente).</li>
                </UL>
                <P>Restricții pentru <Role color="#0D9488">Membru</Role>:</P>
                <UL>
                    <li>Nu pot muta taskuri în prima coloană („De făcut”) sau în „Finalizat” — doar adminii/lead-ul.</li>
                    <li>Nu pot muta în Stand By / CTC / Recurent.</li>
                    <li>Pot muta liber între „În lucru” și „Review”.</li>
                </UL>
            </>
        ),
    },
    {
        id: "taskuri", icon: <CheckSquare size={18} />, title: "Taskuri",
        body: (
            <>
                <P>Un task se deschide cu click pe card. În panoul lateral (drawer) ai tot:</P>
                <UL>
                    <li><strong>Creare</strong> — butonul „Task nou” sau „+” pe o coloană.</li>
                    <li><strong>Responsabili</strong> — poți pune mai mulți; ei primesc notificare + email.</li>
                    <li><strong>Prioritate</strong> — Scăzut / Mediu / Ridicat / Critic.</li>
                    <li><strong>Termen limită cu oră</strong> <Clock size={12} className="inline" /> — acum poți seta și ora, nu doar data. Termenele apar ca „15 iun, 14:30”; cele fără oră rămân „15 iun”. Întârzierile se calculează la minut.</li>
                    <li><strong>Descriere, comentarii, atașamente, etichete</strong> — toate în drawer.</li>
                    <li><strong>Ștergere / Arhivare</strong> — admin sau lead-ul echipei taskului (ștergerea elimină și comentariile/checklist-ul).</li>
                </UL>
            </>
        ),
    },
    {
        id: "duplicare", icon: <Copy size={18} />, title: "Duplicarea unui task",
        body: (
            <>
                <P>Util când ai taskuri asemănătoare și nu vrei să le scrii de la zero.</P>
                <UL>
                    <li>Deschide taskul → butonul <strong>„Duplică”</strong> din antetul drawer-ului.</li>
                    <li>Se deschide un modal <strong>pre-completat</strong> cu toate detaliile (titlul are deja „- duplicat”). Poți alege coloana destinație și modifica orice înainte de salvare.</li>
                    <li>Se copiază: titlu, descriere, responsabili, prioritate, termen, etichete și (opțional) checklist-ul.</li>
                    <li>NU se copiază: comentariile, atașamentele, istoricul și statusul de finalizat — duplicatul pornește curat.</li>
                </UL>
            </>
        ),
    },
    {
        id: "recurente", icon: <Repeat size={18} />, title: "Taskuri recurente",
        body: (
            <>
                <P>Pentru sarcini care se repetă periodic (rapoarte săptămânale, verificări lunare etc.). Se folosesc prin coloana <strong>„Recurent”</strong>.</P>
                <UL>
                    <li><strong>Creezi un „master”</strong> — apeși „+” pe coloana Recurent și alegi <strong>frecvența</strong> (zilnic/săptămânal/lunar), <strong>la fiecare N</strong>, și data+ora primei generări.</li>
                    <li><strong>Transformi un task existent în recurent</strong> <Role color="#002FA7">Admin</Role><Role color="#D97706">Lead</Role> — tragi taskul din orice coloană peste „Recurent”; apare un dialog unde alegi recurența. Taskul devine master (păstrează titlu, descriere, responsabili și checklist).</li>
                    <li><strong>Generare automată</strong> — la fiecare interval, sistemul creează automat o copie a taskului în „De făcut” (cu checklist și responsabili), iar echipa o lucrează normal. Responsabilii sunt notificați.</li>
                    <li><strong>Badge</strong> — masterul arată „🔁 la fiecare X · următoarea: &lt;dată&gt;”.</li>
                    <li><strong>Editezi recurența</strong> — deschizi masterul → secțiunea „Recurență” în drawer: schimbi frecvența, intervalul, data următoare, sau o oprești/repornești.</li>
                    <li><strong>Oprești seria</strong> — muți masterul în „Finalizat”. Generarea se oprește.</li>
                    <li><strong>Opțiune anti-aglomerare</strong> — „nu genera o instanță nouă cât timp cea precedentă nu e finalizată”.</li>
                </UL>
                <Tip>Generarea se face când cineva deschide board-ul după momentul scadent — deci ora setată este orientativă, nu la secundă.</Tip>
            </>
        ),
    },
    {
        id: "checklist", icon: <CheckSquare size={18} />, title: "Checklist (sub-pași)",
        body: (
            <>
                <UL>
                    <li><strong>Adaugă puncte</strong> <Role color="#002FA7">Admin</Role><Role color="#D97706">Lead</Role> — în secțiunea Checklist din task.</li>
                    <li><strong>Bifare</strong> — oricine (inclusiv membri) poate bifa/debifa; bara de progres se actualizează.</li>
                    <li><strong>Editare text</strong> — dublu-click pe text sau iconul creion; corectezi fără să ștergi și să rescrii. Enter salvează, Esc anulează.</li>
                    <li><strong>Responsabili per punct</strong> — atribui unul sau mai mulți responsabili (dintre cei ai taskului) fiecărui punct.</li>
                    <li><strong>Atașamente multiple</strong> <Paperclip size={12} className="inline" /> — fiecare punct poate avea mai multe fișiere; butonul agrafă acceptă mai multe deodată și adaugă (nu înlocuiește). Fiecare fișier are download și ștergere individuală.</li>
                    <li><strong>Șabloane de checklist</strong> — salvezi un checklist ca șablon personal și îl aplici pe alt task.</li>
                </UL>
            </>
        ),
    },
    {
        id: "comentarii", icon: <MessageCircle size={18} />, title: "Comentarii & Atașamente",
        body: (
            <>
                <UL>
                    <li><strong>Comentarii</strong> — pe fiecare task; responsabilii primesc notificare + email când apare un comentariu nou.</li>
                    <li><strong>Atașamente la task</strong> — încarci fișiere direct pe task (separate de cele de pe punctele de checklist).</li>
                </UL>
            </>
        ),
    },
    {
        id: "calendar", icon: <CalendarDays size={18} />, title: "Calendar",
        body: (
            <>
                <UL>
                    <li><strong>Vedere lunară</strong> — navighezi înainte/înapoi/azi.</li>
                    <li><strong>Termenele taskurilor</strong> apar automat (📋), pe data — și ora — scadentă.</li>
                    <li><strong>Evenimente proprii</strong> — click pe o zi → creezi notă, memento, întâlnire online sau fizică (cu oră).</li>
                    <li><strong>Invitații</strong> — la întâlniri poți Accepta / Refuza; cele acceptate îți apar în calendar.</li>
                </UL>
            </>
        ),
    },
    {
        id: "chat", icon: <MessageSquare size={18} />, title: "Chat",
        body: (
            <>
                <UL>
                    <li><strong>Canale de echipă</strong> — create automat pentru fiecare echipă.</li>
                    <li><strong>Mesaje directe (DM)</strong> — conversații 1-la-1.</li>
                    <li><strong>Canale custom</strong> — adminii pot crea canale tematice.</li>
                    <li><strong>Timp real</strong> — mesajele apar instant; ai contoare de necitite în meniu.</li>
                </UL>
            </>
        ),
    },
    {
        id: "asistenta", icon: <HandHelping size={18} />, title: "Asistență",
        body: (
            <>
                <P>Când ai nevoie de ajutor pe un task:</P>
                <UL>
                    <li>În drawer-ul taskului → <strong>„Solicită asistență”</strong>.</li>
                    <li>Alegi persoana, scrii un mesaj și (opțional) propui o întâlnire cu dată/oră.</li>
                    <li>Destinatarul vede cererea la „Asistență” și poate Accepta/Refuza.</li>
                    <li>Dacă acceptă cu întâlnire, evenimentul apare automat în calendarul ambelor persoane.</li>
                </UL>
            </>
        ),
    },
    {
        id: "notificari", icon: <Bell size={18} />, title: "Notificări",
        body: (
            <>
                <UL>
                    <li><strong>În aplicație</strong> — clopoțelul din meniu; click pe o notificare te duce la locul potrivit (task, board, calendar, asistență).</li>
                    <li><strong>Notificări de browser</strong> — la prima utilizare ți se cere permisiunea; primești alerte și când nu ai tab-ul deschis.</li>
                    <li>Tipuri: task atribuit, comentariu nou, task pus în Stand By, instanță recurentă generată, invitații etc.</li>
                </UL>
            </>
        ),
    },
    {
        id: "rapoarte", icon: <BarChart3 size={18} />, title: "Rapoarte",
        body: (
            <>
                <UL>
                    <li><strong>Privire de ansamblu</strong> — total taskuri, finalizate, întârziate, pe priorități/status/responsabil, cu filtre Workspace → Echipă → Board.</li>
                    <li><strong>Top Utilizatori</strong> — scor de productivitate per persoană (volum, rata la timp, întârzieri), cu badge „Top Performer” 🏆 și „Atenție” 📉.</li>
                    <li><strong>Sinteză AI</strong> — generează un rezumat în limbaj natural al situației (admin / Team Lead).</li>
                </UL>
            </>
        ),
    },
    {
        id: "setari", icon: <Settings size={18} />, title: "Setări de profil",
        body: (
            <>
                <UL>
                    <li><strong>Profil</strong> — nume, poreclă, funcție (job title), culoare de avatar.</li>
                    <li><strong>Schimbare parolă</strong> — recomandat la prima logare.</li>
                    <li><strong>Temă</strong> — întunecat/luminos.</li>
                </UL>
            </>
        ),
    },
    {
        id: "admin", icon: <Database size={18} />, title: "Administrare (Manager Departament)",
        body: (
            <>
                <P><Role color="#7C3AED">Super Admin</Role><Role color="#002FA7">Admin</Role> Zona de administrare:</P>
                <UL>
                    <li><strong>Utilizatori</strong> — creezi, editezi, dezactivezi conturi; setezi rolul și un „titlu custom” afișat în interfață.</li>
                    <li><strong>Jurnal de activitate</strong> (audit log) — cine ce a făcut.</li>
                    <li><strong>Setări AI</strong> — cheia și modelul pentru sintezele AI.</li>
                    <li><strong>Backup</strong> — export complet al datelor (inclusiv atașamente) într-un fișier și import/restaurare. Fă export periodic.</li>
                    <li><strong>Toate taskurile</strong> — vedere globală cu filtre (workspace, echipă, status, căutare).</li>
                </UL>
            </>
        ),
    },
    {
        id: "faq", icon: <Lightbulb size={18} />, title: "Sfaturi & întrebări frecvente",
        body: (
            <>
                <UL>
                    <li><strong>Nu pot muta un task într-o coloană</strong> — probabil ești Membru și coloana e restricționată (prima, ultima, Stand By, CTC, Recurent). Cere unui admin/lead.</li>
                    <li><strong>Nu văd butonul „Task nou”</strong> — board-ul nu permite membrilor să creeze taskuri; adminul îl poate activa din setările board-ului.</li>
                    <li><strong>Am făcut o modificare și nu apare</strong> — reîmprospătează pagina (Ctrl+Shift+R) ca să iei ultima versiune.</li>
                    <li><strong>Vreau să opresc un task recurent</strong> — mută masterul (din coloana Recurent) în „Finalizat”.</li>
                    <li><strong>Am greșit un punct de checklist</strong> — dublu-click pe el și editează, nu e nevoie să-l ștergi.</li>
                </UL>
            </>
        ),
    },
];

export default function HowToPage() {
    const [active, setActive] = useState(SECTIONS[0].id);

    const goTo = (id) => {
        setActive(id);
        const el = document.getElementById(`howto-${id}`);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    return (
        <div className="h-full overflow-y-auto bg-[#F8F9FA] dark:bg-[#0B0D10]" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }} data-testid="howto-page">
            <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
                {/* Header */}
                <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 rounded-lg bg-[#002FA7] flex items-center justify-center flex-shrink-0">
                        <BookOpen size={20} className="text-white" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-[#111827] dark:text-[#F3F4F6]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                            Ghid de utilizare TOM
                        </h1>
                        <p className="text-sm text-[#6B7280] dark:text-[#9CA3AF]">Tot ce trebuie să știi despre platformă, într-un singur loc.</p>
                    </div>
                </div>

                <div className="flex gap-8 mt-6">
                    {/* Cuprins (sticky, desktop) */}
                    <nav className="hidden lg:block w-56 flex-shrink-0">
                        <div className="sticky top-4 space-y-0.5">
                            <p className="text-[10px] font-bold uppercase tracking-widest text-[#9CA3AF] px-3 mb-2">Cuprins</p>
                            {SECTIONS.map(s => (
                                <button key={s.id} onClick={() => goTo(s.id)}
                                    className={`w-full flex items-center gap-2 text-left px-3 py-1.5 rounded-md text-sm transition-colors
                                        ${active === s.id
                                            ? "bg-[#EEF2FF] dark:bg-[#1A2338] text-[#002FA7] dark:text-[#6A91FF] font-semibold"
                                            : "text-[#4B5563] dark:text-[#9CA3AF] hover:bg-[#F1F3F5] dark:hover:bg-[#1C222A]"}`}>
                                    <span className="flex-shrink-0">{s.icon}</span>
                                    <span className="truncate">{s.title}</span>
                                </button>
                            ))}
                        </div>
                    </nav>

                    {/* Conținut */}
                    <div className="flex-1 min-w-0 space-y-4 pb-16">
                        {/* Cuprins mobil */}
                        <div className="lg:hidden mb-4">
                            <select value={active} onChange={e => goTo(e.target.value)}
                                className="w-full px-3 py-2 border border-[#E4E4E7] dark:border-[#252B33] rounded-md text-sm bg-white dark:bg-[#1C222A] dark:text-[#F3F4F6]">
                                {SECTIONS.map(s => <option key={s.id} value={s.id}>{s.title}</option>)}
                            </select>
                        </div>

                        {SECTIONS.map(s => (
                            <section key={s.id} id={`howto-${s.id}`} className="scroll-mt-4 bg-white dark:bg-[#151A21] border border-[#E4E4E7] dark:border-[#252B33] rounded-lg p-5">
                                <div className="flex items-center gap-2.5 mb-3">
                                    <span className="w-8 h-8 rounded-md bg-[#EEF2FF] dark:bg-[#1A2338] text-[#002FA7] dark:text-[#6A91FF] flex items-center justify-center flex-shrink-0">
                                        {s.icon}
                                    </span>
                                    <h2 className="text-lg font-bold text-[#111827] dark:text-[#F3F4F6]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                                        {s.title}
                                    </h2>
                                </div>
                                {s.body}
                            </section>
                        ))}

                        <p className="text-center text-xs text-[#9CA3AF] pt-4">
                            Ai o întrebare la care ghidul nu răspunde? Întreabă un Manager Departament.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}
